import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router';
import type { CatalogFilterSchema } from '@/packages/courses/types/schema';
import type {
  CatalogUrlKey,
  CatalogUrlParams,
} from '@/packages/courses/types/url-params';
import {
  getCatalogParamsFromUrl,
  resetCatalogUrlParams,
  setCatalogUrlParam,
} from '@/packages/courses/utils/url-params';

export interface CatalogFiltersActions {
  setParam: (
    key: CatalogUrlKey,
    value: string | number | string[] | undefined
  ) => void;
  reset: (keys: CatalogUrlKey[]) => void;
  setPage: (page: number) => void;
}

export interface CatalogFiltersContextValue {
  params: CatalogUrlParams;
  actions: CatalogFiltersActions;
  schema: CatalogFilterSchema;
}

const CatalogFiltersContext = createContext<CatalogFiltersContextValue | null>(
  null
);

export interface CatalogFiltersProviderProps {
  schema: CatalogFilterSchema;
  children: ReactNode;
}

export const CatalogFiltersProvider = ({
  schema,
  children,
}: CatalogFiltersProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(
    () => getCatalogParamsFromUrl(searchParams),
    [searchParams]
  );

  const setParam = useCallback(
    (key: CatalogUrlKey, value: string | number | string[] | undefined) => {
      setSearchParams((prev) => setCatalogUrlParam(prev, key, value), {
        replace: true,
      });
    },
    [setSearchParams]
  );

  const reset = useCallback(
    (keys: CatalogUrlKey[]) => {
      setSearchParams((prev) => resetCatalogUrlParams(prev, keys), {
        replace: true,
      });
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (page: number) => {
      setParam('page', page);
    },
    [setParam]
  );

  const actions: CatalogFiltersActions = useMemo(
    () => ({
      setParam,
      reset,
      setPage,
    }),
    [setParam, reset, setPage]
  );

  const value: CatalogFiltersContextValue = useMemo(
    () => ({ params, actions, schema }),
    [params, actions, schema]
  );

  return (
    <CatalogFiltersContext.Provider value={value}>
      {children}
    </CatalogFiltersContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCatalogFilters = (): CatalogFiltersContextValue => {
  const ctx = useContext(CatalogFiltersContext);
  if (!ctx) {
    throw new Error(
      'useCatalogFilters must be used within CatalogFiltersProvider'
    );
  }
  return ctx;
};
